import * as React from 'react'
import { useParams } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { ProcessTargetDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  AddTargetsDocument,
  EmployeeForSelectFragment,
  PerformanceTargetManagementDocument,
} from 'generated/resources/graphql'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import Modal from 'modules/core/components/Modal'
import { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'

export const AddTargetsModal: React.FC<{
  onClose: () => void
  alreadySelectedTargetIds: number[]
  alreadySelectedTargets: EmployeeForSelectFragment[]
}> = ({ onClose, alreadySelectedTargets }) => {
  const { reviewId } = useParams()
  const [selectedEmployees, setSelectedEmployees] = React.useState<
    EmployeeForSelectFragmentNoAccess[]
  >([])
  const [addTargets, { loading }] = useMutation(AddTargetsDocument, {
    extractorFn: (data) => data.performanceMutations.bulkCreatePerformanceReviewProcessTarget,
  })

  const { running, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      const response = await addTargets({
        awaitRefetchQueries: true,
        variables: {
          performanceReviewProcessId: Number(reviewId),
          targetsEmployeeIds: selectedEmployees.map((employee) => employee.id),
        },
        refetchQueries: [ProcessTargetDataTableDocument, PerformanceTargetManagementDocument],
      })

      const handled = handleResult(response, () => undefined)

      closePortal()
      return handled
    }
  )

  return (
    <Modal
      title={i18n.t('performance_process.reviews.target_management_addition_title')}
      description={i18n.t('performance_process.reviews.target_management_addition_description')}
      illustration="add-avatar"
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="primary" disabled={running || loading} submit>
            {i18n.t('common.add')}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <EmployeeSelector
          label={i18n.t('common.employees')}
          emptyLabel={i18n.t('common.select')}
          disabled={false}
          value={selectedEmployees}
          onSelect={(employees) => setSelectedEmployees(employees)}
          multiple
          alwaysOpen
          excludeIds={alreadySelectedTargets.map((target) => target.id)}
        />
      </Box>
    </Modal>
  )
}
